import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from "react-router-dom"
import { setModal, selectModal } from '../../../redux/modalSlice'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'

export default function ChangePaymentMethodModal() {
  const dispatch = useDispatch()
  const params = useParams()
  const modal = useSelector(selectModal)
  const dropdownOptions = useSelector(selectDropdownOptions);
  const customerId = params?.id
  const [customerInfo, setCustomerInfo] = useState(null)
  const paymentMethodACHId = dropdownOptions.find((o) => o?.code_key === "payment_method_ach")?.id;

  const inFlightLoanId = useRef(null)

  const getCustomerInformation = async (customerId, forLoanId) => {
    inFlightLoanId.current = forLoanId
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/ach/status`)
    try {
      const response = await request.get()
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightLoanId.current !== forLoanId) return
      if (response.status === 200 || response.status === 201) {
        setCustomerInfo(response?.data?.data)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (inFlightLoanId.current !== forLoanId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm()

  const currentDate = modal.options?.currentDate;
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const loanId = modal.options?.loanId
  const availablePaymentMethods = modal.options?.availablePaymentMethods

  const resetState = () => {
    reset({ payment_method_dropdown_option_id: null })
    setCustomerInfo(null)
    setSaving(false)
  }

  const handleCancel = () => {
    inFlightLoanId.current = null
    resetState()
    dispatch(setModal({ is_active: false }))
  }

  const active = modal.is_active && modal.type === 'change_payment_method_modal'

  // This modal stays mounted in the Footer between openings, so clear the previous loan's selection
  // and the previous customer's ACH status before loading this one.
  useEffect(() => {
    if (active) {
      resetState()
      getCustomerInformation(customerId, loanId)
    }
  }, [active, loanId])

  const onSubmit = async (formData) => {
    const paymentMethodOption = formData.payment_method_dropdown_option_id?.value;
    if (paymentMethodACHId == paymentMethodOption && customerInfo?.ach_revocation_active) {
      dispatch(
        setModal({
          is_active: true,
          type: 'allow_ach_payments_modal',
          options: { customerId: customerId, hasBkConfirmed: customerInfo?.has_bk_confirmed }
        })
      )
    } else {
      setSaving(true)
      request.setEndpoint(
        `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/change_payment_method`
      )
      try {
        if (currentDate) {
          formData.current_date = currentDate;
        }
        formData.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id.value;
        const response = await request.modify(formData)
        if (response.status === 200) {
          modal.options.successCallback();
          handleCancel()
          if (response?.data?.data?.error) {
            dispatch(setNotification({ type: 'warning', body: response.data.data.error }))
          } else {
            dispatch(setNotification({ type: 'success', body: '' }))
          }
        } else {
          setSaving(false)
          dispatch(
            setNotification({ type: 'error', body: `Error: ${response.status}` })
          )
          modal.options.successCallback();
        }
      } catch (error) {
        modal.options.successCallback();
        setSaving(false)
        const errorMessage =
          error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  }

  return active ? (
    <form onSubmit={handleSubmit(onSubmit)} id="change_payment_method_form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-3">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">Change Payment Method</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleCancel}
            ></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-6"
            style={{ minHeight: '90px', overflow: 'hidden' }}
          >
            <div className="columns mb-0 is-flex-direction-column">
              <div className="column">
                <h3>
                  Use this action to change the payment method of all future payments
                </h3>
              </div>
              <div className="column is-7">
                <h3>Select Payment Method <span className="has-text-danger">*</span></h3>
                <Controller
                  name="payment_method_dropdown_option_id"
                  control={control}
                  rules={{ required: "Please select a method" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={(Array.isArray(availablePaymentMethods) && availablePaymentMethods.length > 0) ? availablePaymentMethods.map((o) => ({ label: o.name, value: o.id })) : []}
                      isSearchable={false}
                      classNames={reactSelectClassNames(errors, 'payment_method_dropdown_option_id')}
                      onChange={(val) =>
                        field.onChange({ label: val?.label, value: val?.value })}
                      menuPosition="fixed"
                      styles={{
                        menu: ({ ...css }) => ({
                          ...css,
                          width: 'inherit',
                        }),
                      }}
                      form="change_payment_method_form"
                    />
                  )}
                />
                {errors?.payment_method_dropdown_option_id?.message && <p className="has-text-danger">{errors?.payment_method_dropdown_option_id?.message}</p>}
              </div>
            </div>
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button is-link is-small"
                disabled={saving && 'disabled'}
              >
                {saving ? 'Submitting' : 'Continue'}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null
}
